/**
 * Client fingerprinting utility to generate consistent client identifiers
 * for tracking user sessions and distinguishing between separate clients
 */

export interface ClientInfo {
  clientId: string;
  userAgent: string;
  referrer: string;
  screenResolution: string;
  timezone: string;
  language: string;
}

/**
 * Generates a simple hash from a string
 */
const simpleHash = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
};

/**
 * Gets browser and system information for client fingerprinting
 */
const getBrowserFingerprint = (): string => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  // Canvas fingerprinting
  if (ctx) {
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Gruntled fingerprint', 2, 2);
  }
  const canvasFingerprint = canvas.toDataURL();

  // Collect various browser characteristics
  const fingerprints = [
    navigator.userAgent,
    navigator.language,
    navigator.platform,
    navigator.cookieEnabled.toString(),
    typeof navigator.doNotTrack !== 'undefined' ? navigator.doNotTrack : 'unknown',
    new Date().getTimezoneOffset().toString(),
    window.screen.width + 'x' + window.screen.height,
    window.screen.colorDepth.toString(),
    typeof window.localStorage !== 'undefined' ? 'localStorage' : 'noLocalStorage',
    typeof window.sessionStorage !== 'undefined' ? 'sessionStorage' : 'noSessionStorage',
    canvasFingerprint.slice(-50) // Last 50 chars of canvas fingerprint
  ];

  return simpleHash(fingerprints.join('|'));
};

/**
 * Generates or retrieves a stored client ID
 */
const getOrCreateClientId = (): string => {
  const STORAGE_KEY = 'gruntled_client_id';
  
  try {
    // Try to get existing client ID from localStorage
    const existingId = localStorage.getItem(STORAGE_KEY);
    if (existingId) {
      return existingId;
    }

    // Generate new client ID combining timestamp and browser fingerprint
    const timestamp = Date.now().toString(36);
    const browserFingerprint = getBrowserFingerprint();
    const clientId = `${timestamp}_${browserFingerprint}`;

    // Store for future use
    localStorage.setItem(STORAGE_KEY, clientId);
    return clientId;
  } catch (error) {
    // Fallback if localStorage is not available
    return `session_${Date.now().toString(36)}_${getBrowserFingerprint()}`;
  }
};

/**
 * Collects comprehensive client information
 */
export const getClientInfo = (): ClientInfo => {
  const clientId = getOrCreateClientId();
  
  return {
    clientId,
    userAgent: navigator.userAgent || 'unknown',
    referrer: document.referrer || 'direct',
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
    language: navigator.language || 'unknown'
  };
};

/**
 * Gets a simplified client identifier for debugging purposes
 */
export const getClientIdForDebugging = (): string => {
  const info = getClientInfo();
  return info.clientId.split('_')[0]; // Just the timestamp part for easier reading
};
